"use client";

/**
 * Email/password sign-in form for the inline "Already have an account?" step
 * of SignUpFlowDialog. Success is observed by the parent via
 * `supabase.auth.onAuthStateChange` — this form only performs the call.
 */

import Link from "next/link";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import LogIn from "lucide-react/dist/esm/icons/log-in";
import { useSession } from "@/contexts/SessionContext";
import PasswordInput from "@/components/auth/PasswordInput";
import { AuthFieldShell } from "@/components/auth/auth-form-ui";
import { FormAnticipationBar } from "@/components/ui/loading-anticipation";
import { toUserFriendlyAuthError } from "@/lib/error-reporting";
import { trackAuthSigninCompleted } from "@/lib/analytics/auth-tracking";
import { TurnstileWidget, isTurnstileEnabled } from "@/components/auth/TurnstileWidget";

interface SignInFormProps {
  /** Called right before the sign-in request — persist pending intents here so
   *  the resume flow picks them up once SIGNED_IN fires. */
  onBeforeSubmit?: () => void;
  /** Closes the surrounding dialog when the user follows a navigation link. */
  onNavigate?: () => void;
  /** Field id prefix so two mounted forms never collide. */
  idPrefix?: string;
}

export function SignInForm({ onBeforeSubmit, onNavigate, idPrefix = "signin" }: SignInFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const { signIn } = useSession();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isTurnstileEnabled() && !captchaToken) {
      setError("Please complete the verification challenge.");
      return;
    }

    setLoading(true);
    setError(null);
    onBeforeSubmit?.();

    try {
      const { error: signInError, userId } = await signIn(email.trim(), password, captchaToken ?? undefined);

      if (signInError) {
        setCaptchaToken(null);
        setTurnstileKey((k) => k + 1);
        setError(toUserFriendlyAuthError(signInError.message));
        setLoading(false);
        return;
      }
      if (userId) {
        trackAuthSigninCompleted("email", userId);
      }
      // The parent's auth state change listener handles success.
    } catch {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignIn} className="space-y-3">
      {error && (
        <Alert variant="destructive" className="py-2">
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      )}

      <AuthFieldShell variant="modal">
        <Label htmlFor={`${idPrefix}-email`} className="text-xs">
          Email <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${idPrefix}-email`}
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 h-9 text-sm bg-secondary border-0"
          required
          autoComplete="email"
        />
      </AuthFieldShell>

      <AuthFieldShell variant="modal">
        <Label htmlFor={`${idPrefix}-password`} className="text-xs">
          Password <span className="text-destructive">*</span>
        </Label>
        <PasswordInput
          id={`${idPrefix}-password`}
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 h-9 text-sm"
          required
          showIcon={false}
          autoComplete="current-password"
        />
        <p className="mt-1.5 text-right">
          <Link
            href="/forgot-password"
            onClick={onNavigate}
            className="text-label text-primary hover:underline"
          >
            Forgot password?
          </Link>
        </p>
      </AuthFieldShell>

      <TurnstileWidget key={`${idPrefix}-${turnstileKey}`} onToken={setCaptchaToken} className="flex justify-center" />

      <FormAnticipationBar show={loading} label="Please wait" />

      <Button
        type="submit"
        className="w-full h-10"
        disabled={loading || (isTurnstileEnabled() && !captchaToken)}
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <LogIn className="mr-2 h-4 w-4" />
        )}
        Sign In
      </Button>
    </form>
  );
}
